import { getClient, getClientEndpoint } from "./client";
import { translateError, VixioError } from "./errors";
import { uploadToStorage } from "./files";

/**
 * Envio de arquivo em duas etapas, igual em todos os módulos (portal, entregáveis, chamados, mídia do site):
 *
 *   1. `begin`  RPC do módulo: valida tipo, tamanho e permissão e RESERVA o caminho no bucket (só deste usuário);
 *   2. `put`    o navegador sobe o arquivo direto à API do Storage, com progresso (a política do bucket só aceita o
 *               caminho reservado por este usuário, sem sobrescrever);
 *   3. `finish` RPC do módulo: confere que o objeto chegou (e o tamanho) e libera o arquivo.
 *
 * O navegador confere antes o que o banco confere de novo (tipo pela extensão e tamanho), só para avisar cedo.
 */

/** Regra de arquivos aceitos: extensão → tipo, limite de tamanho e texto para a tela. */
export interface UploadRule {
  /** Extensão (minúscula, sem ponto) → tipo MIME conferido pelo banco e pelo bucket. */
  types: Readonly<Record<string, string>>;
  maxBytes: number;
  /** Ex.: "PDF, imagem, planilha, documento, apresentação, texto, CSV ou ZIP". */
  label: string;
}

const DOCUMENT_TYPES: Record<string, string> = {
  pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif",
  txt: "text/plain", csv: "text/csv", doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint", pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  odt: "application/vnd.oasis.opendocument.text", ods: "application/vnd.oasis.opendocument.spreadsheet", zip: "application/zip",
};

const MB = 1024 * 1024;

/** Documentos do dia a dia (portal, chamados): 25 MB. */
export const DOCUMENT_FILES: UploadRule = {
  types: DOCUMENT_TYPES,
  maxBytes: 25 * MB,
  label: "PDF, imagem, planilha, documento, apresentação, texto, CSV ou ZIP",
};

/** Arquivos de entrega (delivery): mesmos tipos, 50 MB. */
export const DELIVERABLE_FILES: UploadRule = { ...DOCUMENT_FILES, maxBytes: 50 * MB };

/** Mídia do site (bucket público): imagens sem SVG e PDF, 10 MB. */
export const SITE_MEDIA_FILES: UploadRule = {
  types: { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif", avif: "image/avif", pdf: "application/pdf" },
  maxBytes: 10 * MB,
  label: "PNG, JPEG, WebP, GIF, AVIF ou PDF",
};

/** Só imagens do site (capa, logo). */
export const SITE_IMAGE_FILES: UploadRule = {
  types: { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif", avif: "image/avif" },
  maxBytes: 10 * MB,
  label: "PNG, JPEG, WebP, GIF ou AVIF",
};

/** Valor do atributo `accept` do campo de arquivo. */
export function acceptAttribute(rule: UploadRule): string {
  return Object.keys(rule.types).map((e) => `.${e}`).join(",");
}

/** Tipo do arquivo pela extensão (o navegador às vezes não informa, ou informa outro). */
export function contentTypeFor(file: { name: string }, rule: UploadRule): string | null {
  const ext = /\.([A-Za-z0-9]{1,8})$/.exec(file.name)?.[1]?.toLowerCase();
  return ext ? (rule.types[ext] ?? null) : null;
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  if (n < 1024) return `${n} B`;
  if (n < MB) return `${Math.max(1, Math.round(n / 1024)).toLocaleString("pt-BR")} KB`;
  return `${(n / MB).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/** Problema do arquivo para a regra, em pt-BR, ou null. */
export function checkUploadFile(file: { name: string; size: number }, rule: UploadRule): string | null {
  if (!file.name || /[/\\]/.test(file.name) || file.name.length > 200) return "Nome de arquivo inválido.";
  if (file.size === 0) return "O arquivo está vazio.";
  if (file.size > rule.maxBytes) return `O arquivo tem ${formatBytes(file.size)}; o limite é ${formatBytes(rule.maxBytes)}.`;
  if (!contentTypeFor(file, rule)) return `Tipo de arquivo não aceito: envie ${rule.label}.`;
  return null;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0 a 100. */
  pct: number;
}

export interface PutOptions {
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

/** O que a RPC de reserva devolve (cada módulo acrescenta o id do registro). */
export interface UploadReservation {
  bucket: string;
  storage_path: string;
}

export type PutObject = (bucket: string, path: string, file: Blob, contentType: string, o: PutOptions) => Promise<void>;

function storageErrorMessage(status: number, raw: string): string {
  if (status === 413 || /size|large|413/i.test(raw)) return "O arquivo é maior que o permitido.";
  if (status === 415 || /mime|type/i.test(raw)) return "Tipo de arquivo não aceito.";
  if (status === 401 || status === 403 || /row-level|policy|unauthor|403/i.test(raw)) {
    return "Envio não autorizado. Atualize a página e tente de novo.";
  }
  if (status === 409 || /exists|duplicate/i.test(raw)) return "Este envio já foi feito. Atualize a página.";
  return "Não foi possível enviar o arquivo agora. Tente de novo em instantes.";
}

/**
 * Sobe o arquivo à API do Storage com progresso (XMLHttpRequest: o fetch não informa o progresso do envio).
 * Sem XMLHttpRequest (testes, servidor) usa o cliente do Supabase, sem progresso.
 */
export const putObject: PutObject = async (bucket, path, file, contentType, o) => {
  const ep = getClientEndpoint();
  if (typeof XMLHttpRequest === "undefined" || !ep) {
    await uploadToStorage(bucket, path, file, contentType);
    o.onProgress?.({ loaded: file.size, total: file.size, pct: 100 });
    return;
  }
  let token: string | null = null;
  try {
    token = (await getClient().auth.getSession()).data.session?.access_token ?? null;
  } catch (e) {
    throw translateError(e);
  }
  if (!token) throw new VixioError("Sua sessão expirou. Entre de novo para enviar.", "auth");
  const url = `${ep.url}/storage/v1/object/${encodeURIComponent(bucket)}/${path.split("/").map(encodeURIComponent).join("/")}`;
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("apikey", ep.anonKey);
    xhr.setRequestHeader("x-upsert", "false");
    xhr.setRequestHeader("cache-control", "max-age=3600");
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (ev) => {
      const total = ev.lengthComputable ? ev.total : file.size;
      o.onProgress?.({ loaded: ev.loaded, total, pct: total ? Math.min(100, Math.round((ev.loaded / total) * 100)) : 0 });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        o.onProgress?.({ loaded: file.size, total: file.size, pct: 100 });
        resolve();
      } else {
        reject(new VixioError(storageErrorMessage(xhr.status, xhr.responseText ?? ""), "unavailable", String(xhr.status), xhr.responseText ?? null));
      }
    };
    xhr.onerror = () => reject(new VixioError("Falha de conexão durante o envio. Confira a internet e tente de novo.", "network"));
    xhr.onabort = () => reject(new VixioError("Envio cancelado.", "validation", "aborted"));
    if (o.signal) {
      if (o.signal.aborted) {
        xhr.abort();
        return;
      }
      o.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(file.type === contentType ? file : new Blob([file], { type: contentType }));
  });
};

/** Parte do envio retomável: o Storage do Supabase exige partes de exatamente 6 MB (menos a última). */
export const RESUMABLE_CHUNK = 6 * 1024 * 1024;

interface XhrResult {
  status: number;
  header: (name: string) => string | null;
  text: string;
}

function xhrRequest(method: string, url: string, headers: Record<string, string>, body: Blob | null,
                    onProgress?: (loaded: number) => void, signal?: AbortSignal): Promise<XhrResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    if (onProgress) xhr.upload.onprogress = (ev) => onProgress(ev.loaded);
    xhr.onload = () => resolve({ status: xhr.status, header: (n) => xhr.getResponseHeader(n), text: xhr.responseText ?? "" });
    xhr.onerror = () => reject(new VixioError("Falha de conexão durante o envio. Confira a internet e tente de novo.", "network"));
    xhr.onabort = () => reject(new VixioError("Envio cancelado.", "validation", "aborted"));
    if (signal) {
      if (signal.aborted) return xhr.abort();
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  });
}

async function sessionToken(): Promise<string> {
  let token: string | null = null;
  try {
    token = (await getClient().auth.getSession()).data.session?.access_token ?? null;
  } catch (e) {
    throw translateError(e);
  }
  if (!token) throw new VixioError("Sua sessão expirou. Entre de novo para enviar.", "auth");
  return token;
}

const b64 = (s: string): string => btoa(String.fromCharCode(...new TextEncoder().encode(s)));
const memo = {
  get(k: string): string | null {
    try {
      return typeof localStorage === "undefined" ? null : localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string | null): void {
    try {
      if (typeof localStorage === "undefined") return;
      if (v === null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    } catch {
      /* navegador sem armazenamento: o envio só não retoma depois de recarregar a página */
    }
  },
};

/**
 * Envio retomável (protocolo TUS do Storage do Supabase) para arquivos grandes, como gravações de reunião: partes de
 * 6 MB, nova tentativa de cada parte com espera crescente (retoma do ponto confirmado pelo servidor) e retomada depois
 * de recarregar a página (o endereço do envio fica no navegador até concluir). A sessão é renovada a cada parte (um
 * envio longo passa da validade do token). Arquivo de até 6 MB vai pelo envio simples (putObject).
 */
export const putObjectResumable: PutObject = async (bucket, path, file, contentType, o) => {
  const ep = getClientEndpoint();
  if (typeof XMLHttpRequest === "undefined" || !ep || file.size <= RESUMABLE_CHUNK) {
    return putObject(bucket, path, file, contentType, o);
  }
  const endpoint = `${ep.url}/storage/v1/upload/resumable`;
  const base = async (): Promise<Record<string, string>> => ({ Authorization: `Bearer ${await sessionToken()}`, apikey: ep.anonKey, "Tus-Resumable": "1.0.0" });
  // O Storage pode devolver o endereço com o host interno: usa só o identificador.
  const absolute = (loc: string): string => {
    const id = loc.split("/upload/resumable/")[1];
    return id ? `${endpoint}/${id}` : new URL(loc, endpoint).toString();
  };
  const memoKey = `vx-tus:${bucket}/${path}:${file.size}:${(file as File).lastModified ?? 0}`;
  const report = (loaded: number) =>
    o.onProgress?.({ loaded, total: file.size, pct: Math.min(100, Math.round((loaded / file.size) * 100)) });
  const fail = (r: XhrResult): never => {
    throw new VixioError(storageErrorMessage(r.status, r.text), r.status === 401 || r.status === 403 ? "auth" : "unavailable", String(r.status), r.text || null);
  };
  const headOffset = async (loc: string): Promise<number | null> => {
    const r = await xhrRequest("HEAD", loc, await base(), null, undefined, o.signal);
    if (r.status === 404 || r.status === 410) return null;
    if (r.status < 200 || r.status >= 300) fail(r);
    return Number(r.header("Upload-Offset") ?? 0);
  };

  let location = memo.get(memoKey);
  let offset = 0;
  if (location) {
    const at = await headOffset(location).catch(() => null);
    if (at === null) location = null;
    else offset = at;
  }
  if (!location) {
    const r = await xhrRequest("POST", endpoint, {
      ...(await base()),
      "Upload-Length": String(file.size),
      "Upload-Metadata": [["bucketName", bucket], ["objectName", path], ["contentType", contentType], ["cacheControl", "3600"]]
        .map(([k, v]) => `${k} ${b64(v!)}`).join(","),
      "x-upsert": "false",
    }, null, undefined, o.signal);
    if (r.status !== 201 || !r.header("Location")) fail(r);
    location = absolute(r.header("Location")!);
    memo.set(memoKey, location);
    offset = 0;
  }
  report(offset);
  while (offset < file.size) {
    const chunk = file.slice(offset, Math.min(file.size, offset + RESUMABLE_CHUNK));
    for (let attempt = 1; ; attempt++) {
      try {
        const start = offset;
        const r = await xhrRequest("PATCH", location, {
          ...(await base()), "Upload-Offset": String(offset), "Content-Type": "application/offset+octet-stream",
        }, chunk, (loaded) => report(start + loaded), o.signal);
        if (r.status === 409) {
          // O servidor tem outro ponto confirmado: retoma de lá.
          offset = (await headOffset(location)) ?? 0;
          break;
        }
        if (r.status < 200 || r.status >= 300) fail(r);
        offset = Number(r.header("Upload-Offset") ?? start + chunk.size);
        break;
      } catch (e) {
        const err = e instanceof VixioError ? e : translateError(e);
        const transient = err.kind === "network" || (err.kind === "unavailable" && /^5\d\d$/.test(err.code ?? ""));
        if (o.signal?.aborted || !transient || attempt >= 5) {
          if (!transient) memo.set(memoKey, null);
          throw err;
        }
        await new Promise((r) => setTimeout(r, Math.min(30_000, 1000 * 2 ** attempt)));
        offset = (await headOffset(location).catch(() => offset)) ?? offset;
      }
    }
    report(offset);
  }
  memo.set(memoKey, null);
  o.onProgress?.({ loaded: file.size, total: file.size, pct: 100 });
};

export interface TwoStepUpload<T extends UploadReservation, R> {
  rule: UploadRule;
  /** RPC de reserva (recebe o tipo conferido pela extensão). */
  begin: (contentType: string, file: File) => Promise<T>;
  /** RPC de conclusão. */
  finish: (reservation: T, file: File) => Promise<R>;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
  /** Substitui o envio ao Storage (testes). */
  put?: PutObject;
}

/** Os três passos, com a conferência do navegador antes. Erros viram VixioError em pt-BR. */
export async function uploadInTwoSteps<T extends UploadReservation, R>(file: File, o: TwoStepUpload<T, R>): Promise<R> {
  const problem = checkUploadFile(file, o.rule);
  if (problem) throw new VixioError(problem, "validation");
  const contentType = contentTypeFor(file, o.rule)!;
  o.onProgress?.({ loaded: 0, total: file.size, pct: 0 });
  const reservation = await o.begin(contentType, file);
  if (o.signal?.aborted) throw new VixioError("Envio cancelado.", "validation", "aborted");
  await (o.put ?? putObject)(reservation.bucket, reservation.storage_path, file, contentType, { onProgress: o.onProgress, signal: o.signal });
  return o.finish(reservation, file);
}
